/**
 * Server-side CSM (Customer Success Manager) detection utilities
 *
 * Detects CSM/Admin users and routes API calls to the appropriate endpoints
 * (/csm prefix). Employee-pool (cell-manager) tokens use `cognito:groups`
 * (csm / admin). Regular tenant users have neither group.
 */

export interface TokenPayload {
  sub: string;
  email?: string;
  'custom:tenant'?: string;
  'custom:tenant_name'?: string;
  'custom:tenant_id'?: string;
  'custom:current_tenant_id'?: string;
  'custom:org_id'?: string;
  'custom:org_name'?: string;
  'custom:misc1'?: string;  // Org ID with dashes
  'custom:misc2'?: string;  // Tenant ID with dashes
  'custom:misc3'?: string;
  'cognito:groups'?: string[];
  exp?: number;
  iat?: number;
}

/** Cognito groups that identify TAI internal staff (mirrors the authorizer's INTERNAL_GROUPS). */
const INTERNAL_COGNITO_GROUPS = new Set(['csm', 'admin']);

export type UserType = 'csm' | 'admin' | 'tenant_admin';

/**
 * Convert base64url to standard base64
 * JWTs use base64url encoding per RFC 7515, which uses - and _ instead of + and /
 * @param base64url - Base64url encoded string
 * @returns Standard base64 encoded string
 */
function base64UrlToBase64(base64url: string): string {
  // Replace base64url characters with standard base64 characters
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed (base64url omits padding)
  const padding = base64.length % 4;
  if (padding) {
    base64 += '='.repeat(4 - padding);
  }

  return base64;
}

/**
 * Decode a JWT token (server-side compatible)
 * @param token - JWT token string (may be URL-encoded)
 * @returns Decoded token payload or null if invalid
 */
export function decodeToken(token: string | null): TokenPayload | null {
  if (!token) return null;

  try {
    // URL decode if needed (ALB sometimes URL-encodes tokens)
    let decodedToken = token;
    if (token.includes('%')) {
      try {
        decodedToken = decodeURIComponent(token);
      } catch {
        // Token wasn't URL-encoded, use as-isles
      }
    }

    const parts = decodedToken.split('.');
    if (parts.length !== 3) return null;

    // Convert base64url to standard base64, then decode
    // JWTs use base64url encoding per RFC 7515
    const base64Payload = base64UrlToBase64(parts[1]);
    const payload = JSON.parse(Buffer.from(base64Payload, 'base64').toString('utf-8'));
    return payload as TokenPayload;
  } catch (error) {
    console.error('[CSMUtils] Failed to decode token:', error);
    return null;
  }
}

/**
 * Normalize a UUID string by adding dashes if missing
 * UUIDs are 32 characters without dashes, 36 with dashes
 * @param uuid - UUID string (may or may not have dashes)
 * @returns UUID with dashes, or original string if not a valid UUID format
 */
export function normalizeUUID(uuid: string | null): string | null {
  if (!uuid) return null;

  // If it's a UUID without dashes (32 chars), add them
  if (uuid.length === 32 && !uuid.includes('-')) {
    return `${uuid.slice(0, 8)}-${uuid.slice(8, 12)}-${uuid.slice(12, 16)}-${uuid.slice(16, 20)}-${uuid.slice(20)}`;
  }

  return uuid;
}

/**
 * Get user type for routing decisions.
 *
 * Employee-pool (cell-manager) tokens use `cognito:groups` (csm / admin).
 * Regular tenant-pool tokens have neither group → tenant_admin.
 *
 * @param idToken - ID or access token string
 * @returns 'csm', 'admin', or 'tenant_admin'
 */
export function getUserType(idToken: string | null): UserType {
  const payload = decodeToken(idToken);
  if (!payload) return 'tenant_admin';

  const groups = payload['cognito:groups'] ?? [];

  if (groups.includes('admin')) {
    return 'admin';
  }

  if (groups.includes('csm')) {
    return 'csm';
  }

  return 'tenant_admin';
}

/**
 * Extract tenant ID from token claims
 *
 * CSM/Superadmin users may have tenant ID in different claims than regular users.
 * Priority order:
 * 1. custom:misc2 (tenant ID with dashes, set by heimdall after tenant switch)
 * 2. custom:current_tenant_id (with dashes)
 * 3. custom:tenant_id (with dashes)
 * 4. custom:tenant (without dashes, fallback)
 *
 * @param idToken - ID token string
 * @returns Tenant ID (with dashes normalized) or null if not found
 */
export function getTenantIdFromToken(idToken: string | null): string | null {
  const payload = decodeToken(idToken);
  if (!payload) return null;

  // Try different claim fields in priority order
  const tenantId =
    payload['custom:current_tenant_id'] || // Current tenant ID with dashes
    payload['custom:tenant_id'] ||        // Tenant ID with dashes
    null;

  if (!tenantId) return null;

  // Normalize UUID format (add dashes if missing)
  return normalizeUUID(tenantId);
}

/**
 * Get the user-service base path based on user type
 *
 * CSM/Superadmin users use /csm prefix: /api/v1/csm/tenants/{tenant_id}/...
 * Regular tenant admins use: /api/v1/tenants/{tenant_id}/...
 *
 * @param userType - User type from getUserType()
 * @param tenantId - Tenant ID
 * @param path - API path (e.g., '/users', '/groups')
 * @returns Full API path with correct prefix
 */
export function getUserServicePath(userType: UserType, tenantId: string, path: string): string {
  if (userType === 'csm' || userType === 'admin') {
    return `/user-service/v1/csm/tenants/${tenantId}${path}`;
  }
  return `/user-service/v1/tenants/${tenantId}${path}`;
}

/**
 * Log CSM routing decision for debugging
 */
export function logCSMRouting(userType: UserType, tenantId: string | null, basePath: string): void {
  console.log(`[CSMUtils] User type: ${userType}, Tenant ID: ${tenantId}, Routing to: ${basePath}`);
}
